import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ZoneAreasList } from '@/components/features/delivery-zones/ZoneAreasList';
import { ZoneAreasMap } from '@/components/features/delivery-zones/ZoneAreasMap';
import { DeliveryZone, DeliveryZoneArea, NeighborhoodWithPolygon } from '@/types/delivery-zone.types';
import { Card } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { useI18n } from '@/contexts/I18nContext';

export default function DeliveryZoneDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { get, post, delete: deleteArea, loading } = useApi();
  const { toast } = useToast();
  const { tp } = useI18n();
  
  const [zone, setZone] = useState<DeliveryZone | null>(null);
  const [zoneAreas, setZoneAreas] = useState<DeliveryZoneArea[]>([]);
  const [allNeighborhoods, setAllNeighborhoods] = useState<NeighborhoodWithPolygon[]>([]);
  const [neighborhoodsLoading, setNeighborhoodsLoading] = useState(false);
  const [selectedNeighborhoodId, setSelectedNeighborhoodId] = useState<string | undefined>();
  const [togglingNeighborhoodId, setTogglingNeighborhoodId] = useState<string | null>(null);

  const neighborhoodIdsInZone = new Set(zoneAreas.map(area => area.neighborhood_id));
  const areaIdMap = new Map(zoneAreas.map(area => [area.neighborhood_id, area.id]));

  const fetchZoneDetails = async () => {
    if (!id) return;
    try { const response: any = await get(`/api/v1/settings/delivery-zones/${id}`); setZone(response); return response; }
    catch (error) { console.error('Failed to fetch zone details:', error); return null; }
  };

  const fetchZoneAreas = async () => {
    if (!id) return;
    try { const response: any = await get(`/api/v1/settings/delivery-zones/${id}/areas`); setZoneAreas(response || []); }
    catch (error) { console.error('Failed to fetch zone areas:', error); }
  };

  const fetchAllNeighborhoods = async (cityId: string) => {
    setNeighborhoodsLoading(true);
    try { const response: any = await get(`/api/v1/zones?city_id=${cityId}&lang=ar`); setAllNeighborhoods(response || []); }
    catch (error) { console.error('Failed to fetch neighborhoods:', error); }
    finally { setNeighborhoodsLoading(false); }
  };

  const loadData = useCallback(async () => {
    const zoneData = await fetchZoneDetails();
    if (zoneData?.city_id) await Promise.all([fetchZoneAreas(), fetchAllNeighborhoods(zoneData.city_id)]);
    else await fetchZoneAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleBack = () => navigate('/dashboard/settings/delivery-zones');
  const handleRefresh = async () => { await Promise.all([fetchZoneDetails(), fetchZoneAreas()]); };

  const handleToggleNeighborhood = async (neighborhoodId: string) => {
    if (!id || togglingNeighborhoodId) return;
    const inZone = neighborhoodIdsInZone.has(neighborhoodId);
    setTogglingNeighborhoodId(neighborhoodId);
    try {
      if (inZone) {
        const areaId = areaIdMap.get(neighborhoodId);
        if (areaId) { await deleteArea(`/api/v1/settings/delivery-zones/areas/${areaId}`); toast({ title: tp.deliveryZoneDetails.removeSuccessTitle, description: tp.deliveryZoneDetails.removeSuccessDescription }); }
      } else {
        await post(`/api/v1/settings/delivery-zones/${id}/areas`, { neighborhoodId });
        toast({ title: tp.deliveryZoneDetails.addSuccessTitle, description: tp.deliveryZoneDetails.addSuccessDescription });
      }
      await fetchZoneAreas();
    } catch (error) {
      toast({ title: tp.deliveryZoneDetails.errorTitle, description: inZone ? tp.deliveryZoneDetails.removeErrorDescription : tp.deliveryZoneDetails.addErrorDescription, variant: 'destructive' });
    } finally { setTogglingNeighborhoodId(null); }
  };

  if (loading && !zone) return <div className="h-screen flex items-center justify-center" dir="rtl"><LoadingSkeleton type="table" rows={5} /></div>;

  if (!zone) return <div className="h-screen flex items-center justify-center" dir="rtl"><div className="text-center py-12"><p className="text-muted-foreground">{tp.deliveryZoneDetails.notFoundMessage}</p></div></div>;

  return (
    <div className="h-screen flex flex-col overflow-hidden" dir="rtl">
      <div className="flex-shrink-0 px-4 py-4 border-b bg-background">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl md:text-2xl font-bold">{tp.deliveryZoneDetails.pageTitle}</h1>
            <p className="text-muted-foreground text-sm mt-1">
              {tp.deliveryZoneDetails.pageDescription.replace('{name}', zone.name)}
              {zone.city_name && <span className="text-primary"> - {zone.city_name}</span>}
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4 overflow-hidden min-h-0">
        <Card className="p-4 flex flex-col lg:w-1/3 overflow-hidden">
          <ZoneAreasList zone={zone} zoneAreas={zoneAreas} allNeighborhoods={allNeighborhoods} neighborhoodsLoading={neighborhoodsLoading}
            selectedNeighborhoodId={selectedNeighborhoodId} onNeighborhoodSelect={setSelectedNeighborhoodId}
            onToggleNeighborhood={handleToggleNeighborhood} togglingNeighborhoodId={togglingNeighborhoodId}
            onRefresh={handleRefresh} onBack={handleBack}
          />
        </Card>
        <Card className="lg:w-2/3 p-0 overflow-hidden flex-1">
          <ZoneAreasMap allNeighborhoods={allNeighborhoods} zoneAreas={zoneAreas} selectedNeighborhoodId={selectedNeighborhoodId}
            onNeighborhoodSelect={setSelectedNeighborhoodId} onToggleNeighborhood={handleToggleNeighborhood}
            togglingNeighborhoodId={togglingNeighborhoodId} isLoading={neighborhoodsLoading}
          />
        </Card>
      </div>
    </div>
  );
}
